import DomTestingLibrary, {
  FindAllByBoundAttribute,
  findAllByDisplayValue,
  findAllByLabelText,
  findAllByTestId,
  findAllByText,
  getAllByDisplayValue,
  getAllByLabelText,
  getAllByTestId,
  getAllByText,
  getElementError,
} from '@testing-library/dom'
import {
  PktBackLink,
  PktCheckbox,
  PktCombobox,
  PktDatepicker,
  PktHelptext,
  PktIcon,
  PktInputWrapper,
  PktLoader,
  PktProgressbar,
  PktRadioButton,
  PktSelect,
  PktTextarea,
} from '@oslokommune/punkt-elements'
import { AllByBoundAttribute } from '@testing-library/dom/types/queries'

export const PKT_CUSTOM_FORMFIELDS = [
  'pkt-backlink',
  'pkt-checkbox',
  'pkt-combobox',
  'pkt-progressbar',
  'pkt-radiobutton',
  'pkt-select',
  'pkt-textarea',
  'pkt-textinput',
]
const PKT_CUSTOM_ELEMENTS = [
  'pkt-input-wrapper',
  'pkt-icon',
  'pkt-helptext',
  'pkt-loader',
  'pkt-backlink',
  ...PKT_CUSTOM_FORMFIELDS,
]

type PTLElementType =
  | PktBackLink
  | PktCheckbox
  | PktCombobox
  | PktDatepicker
  | PktHelptext
  | PktIcon
  | PktInputWrapper
  | PktLoader
  | PktProgressbar
  | PktRadioButton
  | PktSelect
  | PktTextarea
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement

export type PktTestingLibraryOptions = Pick<
  typeof DomTestingLibrary,
  | 'fireEvent'
  | 'findAllByLabelText'
  | 'findAllByTestId'
  | 'findAllByDisplayValue'
  | 'findAllByText'
  | 'getAllByLabelText'
  | 'getAllByTestId'
  | 'getAllByDisplayValue'
  | 'getAllByText'
>

type TestingLibraryTools = {
  fireEvent: typeof DomTestingLibrary.fireEvent
  findAllByLabelText: typeof DomTestingLibrary.findAllByLabelText
  findAllByTestId: typeof DomTestingLibrary.findAllByTestId
  findAllByDisplayValue: typeof DomTestingLibrary.findAllByDisplayValue
  findAllByText: typeof DomTestingLibrary.findAllByText
  getAllByLabelText: typeof DomTestingLibrary.getAllByLabelText
  getAllByTestId: typeof DomTestingLibrary.getAllByTestId
  getAllByDisplayValue: typeof DomTestingLibrary.getAllByDisplayValue
  getAllByText: typeof DomTestingLibrary.getAllByText
}

export type LabelOrElement<ElementType = PTLElementType> = string | RegExp | ElementType | HTMLLabelElement

export type PktElementValueType = string | number | Date

export type PktTestingLibrary = {
  findPktElementByLabelText: (
    label: string | RegExp | HTMLLabelElement,
    container?: HTMLElement,
  ) => Promise<PTLElementType>
  getPktElementByLabelText: (label: string | RegExp | HTMLLabelElement, container?: HTMLElement) => PTLElementType
  getPktSelectOptions: (labelOrElement: LabelOrElement<PktSelect>, onlySelected?: boolean) => Array<PktOption>
  getAllPktElementsByLabelText: (label: string, container?: HTMLElement) => Element[]
  setPktElementChecked: (labelOrElement: LabelOrElement, checked: boolean) => Promise<boolean>
  isPktElementChecked: (labelOrElement: LabelOrElement) => boolean
  setPktElementValue: (
    labelOrElement: LabelOrElement,
    valueOrValues: PktElementValueType | Array<PktElementValueType>,
    optionsOrUseInputEvent?:
      | boolean
      | {
          useInputEventForNative?: boolean
          useChangeEventForNative?: boolean
          setValuePropForNative?: boolean
        },
  ) => Promise<boolean>
  pktClickButton: (labelOrElement: LabelOrElement<HTMLInputElement | HTMLButtonElement>) => Promise<boolean>
  waitForPktElementsToBeDefined: () => Promise<any>
  setPktSelectedOptionsByLabel: (
    labelOrElement: LabelOrElement<PktSelect>,
    ...desiredOptionLabels: Array<string>
  ) => Promise<any>
  getPktElementByDisplayValue: (labelOrElement: string | RegExp, container?: HTMLElement) => PTLElementType
  findPktElementByDisplayValue: (labelOrElement: string | RegExp, container?: HTMLElement) => Promise<PTLElementType>
}

export const setupPktTestingLibrary: (options: PktTestingLibraryOptions) => PktTestingLibrary = (
  options: PktTestingLibraryOptions,
) => {
  const tools: TestingLibraryTools = {
    fireEvent: options.fireEvent,
    findAllByLabelText: options.findAllByLabelText,
    findAllByDisplayValue: options.findAllByDisplayValue,
    findAllByTestId: options.findAllByTestId,
    findAllByText: options.findAllByText,
    getAllByLabelText: options.getAllByLabelText,
    getAllByDisplayValue: options.getAllByDisplayValue,
    getAllByTestId: options.getAllByTestId,
    getAllByText: options.getAllByText,
  }
  return {
    findPktElementByLabelText: withTestingLibrary(tools, findPktElementByLabel),
    waitForPktElementsToBeDefined,
    getPktElementByLabelText: withTestingLibrary(tools, getPktElementByLabel),
    setPktElementChecked: withTestingLibrary(tools, setPktElementChecked),
    isPktElementChecked: withTestingLibrary(tools, isPktElementChecked),
    getPktSelectOptions: withTestingLibrary(tools, getPktSelectOptions),
    getAllPktElementsByLabelText: withTestingLibrary(tools, getAllPktElementsByLabelText),
    setPktElementValue: withTestingLibrary(tools, setPktElementValue),
    pktClickButton: withTestingLibrary(tools, pktClickButton),
    setPktSelectedOptionsByLabel: withTestingLibrary(tools, setPktSelectedOptionsByLabel),
    getPktElementByDisplayValue: withTestingLibrary(tools, getPktElementByDisplayValue),
    findPktElementByDisplayValue: withTestingLibrary(tools, findPktElementByDisplayValue),
  }
}

type SyncQueries = typeof getAllByText | typeof getAllByLabelText | typeof getAllByTestId | typeof getAllByDisplayValue
type AsyncQueries =
  | FindAllByBoundAttribute
  | typeof findAllByText
  | typeof findAllByLabelText
  | typeof findAllByTestId
  | typeof findAllByDisplayValue

type QueryTypes = 'label' | 'text' | 'displayValue' | 'testid'

const syncQueryFunctionResolver =
  (testingLibrary: TestingLibraryTools) =>
  (queryType: QueryTypes): SyncQueries => {
    const queries: Record<QueryTypes, SyncQueries> = {
      text: testingLibrary.getAllByText,
      label: testingLibrary.getAllByLabelText,
      testid: testingLibrary.getAllByTestId,
      displayValue: testingLibrary.getAllByDisplayValue,
    }
    const query = queries[queryType]
    if (query) {
      return query
    } else {
      throw new Error('Unsupported query type: ' + queryType)
    }
  }

const asyncQueryFunctionResolver =
  (testingLibrary: TestingLibraryTools) =>
  (queryType: QueryTypes): AsyncQueries => {
    const queries: Record<QueryTypes, AsyncQueries> = {
      text: testingLibrary.findAllByText,
      label: testingLibrary.findAllByLabelText,
      testid: testingLibrary.findAllByTestId,
      displayValue: testingLibrary.findAllByDisplayValue,
    }
    const query = queries[queryType]
    if (query) {
      return query
    } else {
      throw new Error('Unsupported query type: ' + queryType)
    }
  }

const syncResolveIdentifierOrElement =
  (testingLibrary: TestingLibraryTools) =>
  <ElementType>(elementOrIdentifier: string | RegExp | HTMLElement, query: QueryTypes) =>
    typeof elementOrIdentifier === 'string' || elementOrIdentifier instanceof RegExp
      ? (getPktElementBy(testingLibrary)(query, elementOrIdentifier) as ElementType)
      : (elementOrIdentifier as ElementType)

const getByLabelElement = <ElementType>(possibleLabelElement: HTMLLabelElement) => {
  const control = possibleLabelElement instanceof HTMLLabelElement && (possibleLabelElement.control as ElementType)
  if (control) {
    return control
  } else {
    throw new Error('Fant ikke noe skjema-element for labelen: ' + possibleLabelElement.outerHTML)
  }
}

const asyncResolveIdentifierOrElement =
  (testingLibrary: TestingLibraryTools) =>
  async <ElementType>(elementOrIdentifier: string | RegExp | HTMLElement, query: QueryTypes) =>
    (elementOrIdentifier instanceof HTMLLabelElement && getByLabelElement<ElementType>(elementOrIdentifier)) ||
    (typeof elementOrIdentifier === 'string' || elementOrIdentifier instanceof RegExp
      ? ((await findPktElementBy(testingLibrary)(query, elementOrIdentifier)) as ElementType)
      : (elementOrIdentifier as ElementType))

const asyncFuncWithStringIdentifierOrElement =
  (testingLibrary: TestingLibraryTools) =>
  <ElementType, ResultType>(
    func: (element: ElementType, ...restArgs: Array<any>) => Promise<ResultType>,
    query: QueryTypes = 'label',
  ) => {
    return async (labelOrPktElement: string | RegExp | HTMLElement, ...restArgs: Array<any>) => {
      const element = await asyncResolveIdentifierOrElement(testingLibrary)<ElementType>(labelOrPktElement, query)
      return await func(element, ...restArgs)
    }
  }

const syncFuncWithStringIdentifierOrElement =
  (testingLibrary: TestingLibraryTools) =>
  <ElementType, ResultType>(
    func: (element: ElementType, ...restArgs: Array<any>) => ResultType,
    query: QueryTypes = 'label',
  ) => {
    return (labelOrPktElement: string | RegExp | HTMLElement, ...restArgs: Array<any>) => {
      const element = syncResolveIdentifierOrElement(testingLibrary)<ElementType>(labelOrPktElement, query)
      return func(element, ...restArgs)
    }
  }

const withTestingLibrary = <F>(testingLibrary: TestingLibraryTools, fn: (testingLibrary: TestingLibraryTools) => F) =>
  fn(testingLibrary)

/**
 * Sørger for at alle brukte custom elements fra Punkt er definerte og erstattet med implementasjoner.
 * Bør kalles etter `render`, før noen operasjoner og antakelser gjøres.
 *
 * F.eks.:
 * <code>
 * render(<div><PktTextinput id="textinput" name={'textInput'} label="My text input" aria-label="My text input" /></div>);
 * await waitForPktElementsToBeDefined();
 *
 * await setPktElementValue("My text input", "new value");
 * </code>
 */
const waitForPktElementsToBeDefined = async () =>
  await Promise.all(
    PKT_CUSTOM_ELEMENTS.map((elementName) => {
      if (document.querySelector(elementName) !== null) {
        return window.customElements.whenDefined(elementName)
      }
    }).filter((promise) => promise),
  )

const findPossibleWrappingPktCustomElement =
  (testingLibrary: TestingLibraryTools) =>
  <R extends PTLElementType>(innerElement: HTMLElement | null, isByRole: boolean, args: Array<any>): PTLElementType => {
    if (!innerElement === null) {
      throw getElementError(`Finner ikke noe element med ${isByRole ? 'role' : 'label'} "${args[0]}"`, document.body)
    }
    const pktElement = innerElement?.closest(PKT_CUSTOM_FORMFIELDS.join(', ')) as R | null
    if (pktElement) {
      return pktElement as R
    } else {
      return innerElement as R
    }
  }

const getPktElementBy =
  (testingLibrary: TestingLibraryTools) =>
  (query: QueryTypes = 'label', identifier: string | RegExp, container?: HTMLElement): PTLElementType => {
    try {
      const queryFunc = syncQueryFunctionResolver(testingLibrary)(query)
      const innerElement = doElementSearch(identifier, query, queryFunc, container)[0]
      return findPossibleWrappingPktCustomElement(testingLibrary)(innerElement, false, [])
    } catch (e) {
      return fallbackSearchForPktSelectByLabel(testingLibrary)(identifier, query, container)
    }
  }

const removeElementBySelector = (ancestor: HTMLElement, selector: string) => {
  const elements = Array.from(ancestor.querySelectorAll(selector))
  elements.forEach((element) => {
    element.parentNode?.removeChild(element)
  })
}

function getPureLabelText(label: Node) {
  const clonedLabel = label.cloneNode(true) as HTMLElement
  removeElementBySelector(clonedLabel, 'pkt-helptext')
  removeElementBySelector(clonedLabel, '.pkt-input-suffix')
  removeElementBySelector(clonedLabel, '.pkt-input-prefix')
  removeElementBySelector(clonedLabel, '.pkt-input-icon')
  removeElementBySelector(clonedLabel, '.pkt-input__counter')
  removeElementBySelector(clonedLabel, 'option')
  removeElementBySelector(clonedLabel, 'pkt-listbox')
  removeElementBySelector(clonedLabel, '.pkt-alert--error')
  removeElementBySelector(clonedLabel, '.pkt-tag')
  removeElementBySelector(clonedLabel, '.pkt-input-check__input-helptext')
  removeElementBySelector(clonedLabel, '.pkt-inputwrapper__helptext')
  return clonedLabel.textContent?.trim() || null
}

const getPureLabelTextForLabelOwner = (labelOwner: HTMLElement): string | null => {
  const label =
    ('labels' in labelOwner &&
      labelOwner.labels instanceof NodeList &&
      labelOwner.labels.length > 0 &&
      labelOwner.labels[0]) ||
    (['input', ...PKT_CUSTOM_FORMFIELDS].includes(labelOwner.tagName.toLowerCase()) &&
      (labelOwner.querySelector('label') as HTMLLabelElement | null)) ||
    null
  if (label) {
    return getPureLabelText(label)
  } else {
    return null
  }
}

const labelMatcher = (labelTextToMatch: string) => (nodeContent: string, element: Element | null) => {
  if (element instanceof HTMLElement) {
    const labelWithoutHelptext = getPureLabelTextForLabelOwner(element as HTMLElement)
    return labelWithoutHelptext === labelTextToMatch
  } else {
    return false
  }
}

const fallbackSearchForPktSelectByLabel =
  (testingLibrary: TestingLibraryTools) =>
  (identifier: any, query: QueryTypes, container: HTMLElement = document.body): PTLElementType => {
    if (typeof identifier === 'string' && query === 'label') {
      // Spesial-case for <pkt-select> som ikke har aria-label
      const matchingLabel: HTMLLabelElement | undefined = Array.from(container.querySelectorAll('label')).find(
        (labelElement) => getPureLabelText(labelElement) === identifier,
      )
      const labelOwner =
        matchingLabel?.control || matchingLabel?.closest('pkt-select') || matchingLabel?.closest('pkt-combobox')
      if (!labelOwner) {
        throw getElementError(`Fant ikke noe element med label "${identifier}"`, container)
      }
      return labelOwner as PTLElementType
    }
    throw getElementError(`Fant ikke noe element med ${query} "${identifier}"`, container)
  }

const doElementSearch = <T>(
  identifier: string | RegExp | HTMLLabelElement,
  query: QueryTypes,
  queryFunc: (...args: Parameters<AllByBoundAttribute>) => T,
  container: HTMLElement = document.body,
): T => {
  return identifier instanceof HTMLLabelElement
    ? getByLabelElement(identifier)
    : typeof identifier === 'string' && query === 'label'
      ? queryFunc(container, labelMatcher(identifier.trim()))
      : queryFunc(container, identifier)
}

const findPktElementBy =
  (testingLibrary: TestingLibraryTools) =>
  async (
    query: QueryTypes = 'label',
    identifier: string | RegExp | HTMLLabelElement,
    container?: HTMLElement,
  ): Promise<PTLElementType> => {
    try {
      if (identifier instanceof HTMLLabelElement) {
        return Promise.resolve(getByLabelElement<PTLElementType>(identifier))
      }
      const queryFunc = asyncQueryFunctionResolver(testingLibrary)(query)
      const innerElement = (await doElementSearch(identifier, query, queryFunc, container))[0]
      return Promise.resolve(findPossibleWrappingPktCustomElement(testingLibrary)(innerElement, false, []))
    } catch (e) {
      return fallbackSearchForPktSelectByLabel(testingLibrary)(identifier, query, container)
    }
  }

const getPktElementByLabel =
  (testingLibrary: TestingLibraryTools) => (label: string | RegExp | HTMLLabelElement, container?: HTMLElement) =>
    label instanceof HTMLLabelElement
      ? getByLabelElement<PTLElementType>(label)
      : getPktElementBy(testingLibrary)('label', label, container)

const findPktElementByLabel =
  (testingLibrary: TestingLibraryTools) =>
  async (label: string | RegExp | HTMLLabelElement, container?: HTMLElement): Promise<PTLElementType> =>
    label instanceof HTMLLabelElement
      ? getByLabelElement<PTLElementType>(label)
      : findPktElementBy(testingLibrary)('label', label, container)

const getPktElementByText = (testingLibrary: TestingLibraryTools) => (text: string | RegExp, container?: HTMLElement) =>
  getPktElementBy(testingLibrary)('text', text, container)

const findPktElementByText =
  (testingLibrary: TestingLibraryTools) => (text: string | RegExp, container?: HTMLElement) =>
    findPktElementBy(testingLibrary)('text', text, container)

const setPktElementChecked = (testingLibrary: TestingLibraryTools) =>
  asyncFuncWithStringIdentifierOrElement(testingLibrary)(async (element: PTLElementType, checked: boolean) => {
    if (!('checked' in element)) {
      throw new Error('Bare elementer som har en "checked"-attributt støttes')
    }
    let returnValue: boolean = false
    if (element.tagName === 'INPUT') {
      // https://github.com/testing-library/react-testing-library/issues/175#issuecomment-637349276
      // kentcdodds: "this should probably be documented better, but with checkboxes you don't actually fire change
      // events, you should fire click events instead."
      const htmlInputElement = element as HTMLInputElement
      if (htmlInputElement.type === 'radio') {
        if (!checked) {
          throw new Error("Kan ikke av-velge en <input type='radio'> - prøv å velge en annen radioknapp i samme gruppe")
        } else {
          returnValue = testingLibrary.fireEvent.click(element)
          await new Promise((resolve) => setTimeout(resolve, 0))
        }
      } else if (htmlInputElement.type === 'checkbox') {
        if (htmlInputElement.checked !== checked) {
          returnValue = testingLibrary.fireEvent.click(element)
        }
      }
    } else {
      returnValue = testingLibrary.fireEvent.change(element, { target: { checked } })
    }

    return Promise.resolve(returnValue)
  })

const isPktElementChecked =
  (testingLibrary: TestingLibraryTools) =>
  (target: LabelOrElement): boolean => {
    const element = syncResolveIdentifierOrElement(testingLibrary)<PTLElementType>(target, 'label')

    if ('checked' in element) return !!(element as HTMLInputElement).checked

    const descendant = element.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')
    return descendant !== null
  }

const getPktElementByDisplayValue =
  (testingLibrary: TestingLibraryTools) => (text: string | RegExp, container?: HTMLElement) =>
    getPktElementBy(testingLibrary)('displayValue', text, container)

const findPktElementByDisplayValue =
  (testingLibrary: TestingLibraryTools) => (text: string | RegExp, container?: HTMLElement) =>
    findPktElementBy(testingLibrary)('displayValue', text, container)

/**
 * Representerer en option i en PktSelect.
 *
 * @property {string} 0 - verdien (value)
 * @property {string | null} 1 - Tekstinnholdet, <option>tekstinnhold</option>
 * @property {boolean} 2 - Om denne er valgt (selected)
 */
export type PktOption = [string, string | undefined, boolean]

const getPktSelectOptions = (testingLibrary: TestingLibraryTools) =>
  syncFuncWithStringIdentifierOrElement(testingLibrary)<PktSelect, Array<PktOption>>(
    (selectElement: PktSelect, onlySelected?: boolean): Array<PktOption> => {
      const optionElements: Array<HTMLOptionElement> = Array.from(
        selectElement.querySelectorAll('option:not(.pkt-hide), data:not(.pkt.hide)'),
      )
      const filter = onlySelected ? ([, , selected]: PktOption) => selected : (_: PktOption) => true
      const currentValue = selectElement.value
      return optionElements
        .map(
          (optionElement) =>
            [optionElement.value, optionElement.textContent?.trim(), optionElement.value === currentValue] as PktOption,
        )
        .filter(filter)
    },
  )

const getAllPktElementsByLabelText =
  (testingLibrary: TestingLibraryTools) =>
  (label: string, container?: HTMLElement): Element[] => {
    const innerElements = testingLibrary.getAllByLabelText(container || document.body, label)
    return innerElements.map((element) => findPossibleWrappingPktCustomElement(testingLibrary)(element, false, []))
  }

const setPktElementValue = (testingLibrary: TestingLibraryTools) =>
  asyncFuncWithStringIdentifierOrElement(testingLibrary)(
    async (
      element: PTLElementType,
      valueOrValues: PktElementValueType | Array<PktElementValueType>,
      optionsOrUseInputEvent:
        | boolean
        | {
            useInputEventForNative?: boolean
            useChangeEventForNative?: boolean
            setValuePropForNative?: boolean
          } = false,
    ): Promise<boolean> => {
      const options =
        typeof optionsOrUseInputEvent === 'object'
          ? optionsOrUseInputEvent
          : // Hvis det er en boolean, så vær bakoverkompatibel
            (optionsOrUseInputEvent && {
              useInputEventForNative: true,
              useChangeEventForNative: true,
              setValuePropForNative: true,
            }) || {
              useInputEventForNative: false,
              useChangeEventForNative: true,
              setValuePropForNative: false,
            }
      if (Array.isArray(valueOrValues) && valueOrValues.length > 1 && element.tagName === 'PKT-SELECT') {
        throw new Error('Multi-verdi <pkt-select> støttes ikke. Bruk <pkt-combobox> i stedet.')
      }
      if (element.tagName === 'PKT-SELECT' || element.tagName === 'PKT-COMBOBOX') {
        const pktSelect = element as HTMLSelectElement
        const multiple = pktSelect.multiple
        const valueAsArray = Array.isArray(valueOrValues) ? valueOrValues : [valueOrValues]
        const newValue = (multiple && valueAsArray) || (valueAsArray.length == 0 && '') || valueAsArray[0]
        testingLibrary.fireEvent.change(element, { target: { value: newValue } })
        return Promise.resolve(true)
      } else {
        if (options.setValuePropForNative) {
          if ('value' in element) {
            element.value = valueOrValues.toString()
          }
        }
        if (options.useInputEventForNative) {
          testingLibrary.fireEvent.input(element, { target: { value: valueOrValues } })
        }
        if (options.useChangeEventForNative) {
          testingLibrary.fireEvent.change(element, { target: { value: valueOrValues } })
        }
        return Promise.resolve(true)
      }
    },
  )

const containsRadioInput = (element: Element): boolean => {
  if (element.tagName === 'INPUT' && (element as HTMLInputElement).type === 'radio') return true

  if (element.querySelector?.('input[type="radio"]')) return true

  if (element.tagName === 'LABEL') {
    const label = element as HTMLLabelElement
    const control = label.control || (label.htmlFor ? document.getElementById(label.htmlFor) : null)
    return !!(control && (control as HTMLInputElement).type === 'radio')
  }

  return false
}

const pktClickButton = (testingLibrary: TestingLibraryTools) =>
  asyncFuncWithStringIdentifierOrElement(testingLibrary)(async (element: PTLElementType) => {
    if (containsRadioInput(element)) {
      throw new Error('Klikk på <pkt-radiobutton> støttes ikke - bruk setPktElementChecked i stedet')
    }
    return testingLibrary.fireEvent.click(element)
  }, 'text')

const setPktSelectedOptionsByLabel = (testingLibrary: TestingLibraryTools) =>
  asyncFuncWithStringIdentifierOrElement(testingLibrary)(
    async (pktSelect: PktSelect, ...desiredOptionLabels: Array<string>) => {
      const availableOptions = getPktSelectOptions(testingLibrary)(pktSelect)
      const selectedOptions = desiredOptionLabels
        .map((optionLabel) => availableOptions.find(([_, label]) => label === optionLabel))
        .filter((possibleOption) => possibleOption) as Array<PktOption>
      if (selectedOptions.length !== desiredOptionLabels.length) {
        throw new Error(
          "Noen av option'ene finnes ikke i denne komponenten. Du valgte " +
            JSON.stringify(desiredOptionLabels) +
            ', mens valgmulighetene er ' +
            JSON.stringify(availableOptions.map(([, label]) => label)),
        )
      }

      return await setPktElementValue(testingLibrary)(
        pktSelect,
        selectedOptions.map(([value]) => value),
      )
    },
  )
